import type {
  IRoutingGraphBuilder,
  linkData,
  RoutingGraph,
  RoutingGraphEdge,
  RoutingGraphNode,
  GraphsMap,
} from "../types/routing";
import { FLOOR_OPTIONS } from "@/config/constants";
import type { FeatureCollection } from "geojson";
import {
  addEdgeToEdgeMap,
  addNodeToNodeMap,
  findCoordsWithFloorForFacility,
  findNodeInGraphs,
  generateTwoEdgesFromTwoNodes,
  generateNodeFromLineFeature,
  loadManualGraphData,
  updateNearestNodeToDoorNode,
  createNearestNodeOnLine,
} from "../utils/routingUtils";
import { fetchScaledMapData } from "@/utils/mapDataUtils";
import { ROUTING_CONFIG } from "../config/constants";
import { RoutingGraphCacheManager } from "./RoutingGraphCacheManager";

export class ManualRoutingGraphBuilder implements IRoutingGraphBuilder {
  private routingGraphCacheManager: RoutingGraphCacheManager;
  private routingGraphMapCache: GraphsMap | null = null;
  private buildingPromise: Promise<GraphsMap> | null = null;

  constructor() {
    this.routingGraphCacheManager = new RoutingGraphCacheManager();
    this.loadGraphCacheOnInit();
  }

  /**
   * 起動時にローカルキャッシュからデータを読み込み、クラス内部に保持する
   */
  private loadGraphCacheOnInit(): void {
    this.routingGraphMapCache = this.routingGraphCacheManager.getCache();
  }

  /**
   * グラフデータのメモリとブラウザキャッシュを更新する
   * @param newGraphs
   */
  public updateGraphMapCache(newGraphs: GraphsMap): void {
    // メモリ変数のグラフデータ更新
    this.routingGraphMapCache = newGraphs;

    // ブラウザキャッシュのグラフデータ更新
    this.routingGraphCacheManager.setCache(newGraphs);
  }

  async buildAll(): Promise<Map<string, RoutingGraph>> {
    if (this.routingGraphMapCache) {
      return this.routingGraphMapCache;
    }
    // 起動後の事前構築と経路探索が重なっても二重に組み立てないよう、構築中の Promise を共有する
    if (!this.buildingPromise) {
      this.buildingPromise = this.buildAllFromSource().finally(() => {
        this.buildingPromise = null;
      });
    }
    return this.buildingPromise;
  }

  private async buildAllFromSource(): Promise<Map<string, RoutingGraph>> {
    const startTime = Date.now();

    const graphsMap = new Map<string, RoutingGraph>();
    const polygonsMap = new Map<string, FeatureCollection>();
    const iconsMap = new Map<string, FeatureCollection>();

    const availableFloors = FLOOR_OPTIONS;
    const createGraphPromises = availableFloors.map(async (floorOption) => {
      const path = floorOption.path;
      const floorId = floorOption.id;
      try {
        const { scaledPolygons, scaledIconsGeoJSON } = await fetchScaledMapData(path);
        polygonsMap.set(floorId, scaledPolygons);
        iconsMap.set(floorId, scaledIconsGeoJSON);
        const scaledGraphDataGeoJson = await loadManualGraphData(floorId);
        const graph = this.createManualGraph(scaledGraphDataGeoJson, floorId);

        graphsMap.set(floorId, graph);
      } catch (error) {
        console.error(`Failed to create graph for ${floorId}:`, error);
      }
    });

    await Promise.all(createGraphPromises);

    try {
      await this.addLinkNodesToGraph(graphsMap, polygonsMap, iconsMap);
    } catch (error) {
      console.error("Failed to add link nodes:", error);
    }

    const elapsed = Date.now() - startTime;
    console.log(`All graphs built in ${elapsed}ms`);

    //cacheがない場合にはcacheを作成
    this.updateGraphMapCache(graphsMap);

    return graphsMap;
  }

  createManualGraph(scaledEdgeData: FeatureCollection, floorId: string): RoutingGraph {
    const nodes = new Map<string, RoutingGraphNode>();
    const edges = new Map<string, RoutingGraphEdge[]>();

    //ノードデータ変換
    scaledEdgeData.features
      .filter((lineFeature) => {
        //このfilterなくてもうまくいくように改良できる気がする
        const properties = lineFeature.properties;
        if (!properties) return false;
        return properties.floorId === floorId;
      })
      .forEach((lineFeature) => {
        //lineStringの両端の点をノードとする
        const generatedNodesResult = generateNodeFromLineFeature(lineFeature);
        if (!generatedNodesResult) return; //continueみたいなもん

        const [p1_node, p2_node] = generatedNodesResult;
        addNodeToNodeMap(p1_node.id, p1_node, nodes);
        addNodeToNodeMap(p2_node.id, p2_node, nodes);

        //１つのLineStringに対して２方向にエッジを作成
        const [edge_forward, edge_back] = generateTwoEdgesFromTwoNodes(p1_node, p2_node);
        addEdgeToEdgeMap(edge_forward.from, edge_forward, edges);
        addEdgeToEdgeMap(edge_back.from, edge_back, edges);
      });

    return {
      nodes,
      edges,
      floorId,
    };
  }

  /**
   * リンクデータを読み込み、階段・エレベーターのノードを追加
   */
  async addLinkNodesToGraph(
    graphs: Map<string, RoutingGraph>,
    scaledPolygonsMap: Map<string, FeatureCollection>,
    scaledIconsMap: Map<string, FeatureCollection>,
    linkDataPath: string = ROUTING_CONFIG.linkDataPath,
  ): Promise<void> {
    // 1. リンクデータを読み込み
    let linkData: linkData;
    try {
      const response = await fetch(linkDataPath);
      if (!response.ok) {
        throw new Error(`Failed to load linkData: ${response.status}`);
      }
      linkData = await response.json();
    } catch (error) {
      console.error("Failed to load link data:", error);
      return;
    }

    // 2. 各リンクを処理
    for (const link of linkData.links) {
      const linkNodeIds: string[] = [];

      // 3. 各施設の重心もしくは対応するアイコンに最も近いノードを特定してリンクノードとする
      for (const facility of link.facilities) {
        const graph = graphs.get(facility.floorId);
        const scaledPolygons = scaledPolygonsMap.get(facility.floorId);

        if (!graph || !scaledPolygons) continue;

        const linkFacilityCoords = await findCoordsWithFloorForFacility(
          facility.facilityId,
          scaledPolygonsMap,
          scaledIconsMap,
          graphs,
        );
        if (!linkFacilityCoords) continue;

        let linkNode = await createNearestNodeOnLine(
          graph,
          linkFacilityCoords.position.x,
          linkFacilityCoords.position.y,
        );
        if (!linkNode) continue;

        //対応するドアが存在する場合、ドア座標を開始or終端ノードにできるようにエッジ追加
        if (linkFacilityCoords.isDoor) {
          //ドアがある場合には作成したドアノードをリンクノードにする
          linkNode = updateNearestNodeToDoorNode(graph, linkNode, linkFacilityCoords);
        }

        linkNodeIds.push(linkNode.id);
      }

      // 4. リンクノード間にエッジを作成
      for (let i = 0; i < linkNodeIds.length; i++) {
        for (let j = i + 1; j < linkNodeIds.length; j++) {
          const nodeAId = linkNodeIds[i];
          const nodeBId = linkNodeIds[j];

          if (nodeAId === undefined || nodeBId === undefined) {
            console.warn("リンクノード間のエッジ作成中にリンクノードを取得するのに失敗しました。");
            continue;
          }
          const nodeA = findNodeInGraphs(graphs, nodeAId);
          const nodeB = findNodeInGraphs(graphs, nodeBId);

          if (!nodeA || !nodeB) continue;

          // フロア差に基づくコスト計算
          /*
          3階→2階→1階と移動するときに3階→1階へと直接移動するようにfloorDiffにsqrtを使用
          もし、1階ずつ移動したい場合にはsqrtをpow(~, 2)に変更するといい
          */
          const floorDiff = Math.sqrt(Math.abs(nodeA.floor - nodeB.floor));
          const baseCost = ROUTING_CONFIG.floorChangeCost;
          const weight =
            link.type === "stairs"
              ? ROUTING_CONFIG.stairsCostMultiplier
              : ROUTING_CONFIG.elevatorCostMultiplier;
          const cost = floorDiff * baseCost * weight;

          const graphA = graphs.get(nodeA.floorId)!;
          const graphB = graphs.get(nodeB.floorId)!;

          //リンクノード間のエッジ作成
          const [edgeAB, edgeBA] = generateTwoEdgesFromTwoNodes(nodeA, nodeB, cost, link.type);
          addEdgeToEdgeMap(nodeAId, edgeAB, graphA.edges);
          addEdgeToEdgeMap(nodeBId, edgeBA, graphB.edges);
        }
      }
    }
  }
}
